import React, { useState, useEffect } from 'react';
import { Card, Button, Typography, Tooltip, Space } from 'antd';
import { CopyOutlined, DownloadOutlined } from '@ant-design/icons';
import { OPLGenerator, OPLStatement } from './OPLGenerator';

const { Text } = Typography;

interface OPLPanelProps {
  graphData: { nodes: any[]; edges: any[] };
  elementAddHistory?: Array<{
    id: string;
    type: 'node' | 'edge';
    timestamp: number;
  }>;
  onHighlightElements?: (elementIds: string[]) => void;
  onFocusElements?: (elementIds: string[]) => void;
  onClearHighlight?: () => void;
}

const OPLPanel: React.FC<OPLPanelProps> = ({
  graphData,
  elementAddHistory,
  onHighlightElements,
  onFocusElements,
  onClearHighlight,
}) => {
  const [oplStatements, setOplStatements] = useState<OPLStatement[]>([]);
  const [oplDocument, setOplDocument] = useState<string>('');
  const [highlightedStatement, setHighlightedStatement] = useState<
    string | null
  >(null);

  // 当图数据变化时重新生成 OPL 语句
  useEffect(() => {
    console.log('🔄 OPL面板检测到数据变化:', {
      图数据: graphData,
      节点数: graphData?.nodes?.length || 0,
      边数: graphData?.edges?.length || 0,
      历史记录数: elementAddHistory?.length || 0,
      时间戳: (graphData as any)?._timestamp,
    });

    if (
      graphData &&
      (graphData.nodes.length > 0 || graphData.edges.length > 0)
    ) {
      console.log('🔤 开始重新生成OPL语句...');
      const generator = new OPLGenerator(graphData);

      // 如果有元素历史记录，使用历史记录生成OPL语句
      let statements: OPLStatement[];
      if (elementAddHistory && elementAddHistory.length > 0) {
        console.log('📋 使用历史记录生成OPL语句');
        statements =
          generator.generateOPLStatementsWithHistory(elementAddHistory);
      } else {
        console.log('📋 使用默认方式生成OPL语句');
        statements = generator.generateOPLStatements();
      }

      const document = generator.generateOPLDocument();

      setOplStatements(statements);
      setOplDocument(document);

      console.log('✅ OPL语句生成完成:', {
        语句数: statements.length,
        语句列表: statements.map(s => ({
          id: s.id,
          text: s.text,
          type: s.type,
        })),
      });
    } else {
      console.log('🧹 清空OPL语句（无数据）');
      setOplStatements([]);
      setOplDocument('');
    }
  }, [graphData, elementAddHistory]);

  // 高亮语句对应的元素（鼠标悬停）
  const handleHighlightStatement = (statement: OPLStatement) => {
    console.log('高亮语句元素:', statement);
    setHighlightedStatement(statement.id);

    if (onHighlightElements) {
      onHighlightElements(statement.elementIds);
    }
  };

  // 清除高亮（鼠标离开）
  const handleClearHighlight = () => {
    console.log('清除高亮');
    setHighlightedStatement(null);

    if (onClearHighlight) {
      onClearHighlight();
    }
  };

  // 聚焦到语句对应的元素（点击）
  const handleFocusStatement = (statement: OPLStatement) => {
    console.log('聚焦语句元素:', statement);

    if (onFocusElements) {
      onFocusElements(statement.elementIds);
    }
  };

  // 复制 OPL 文档到剪贴板
  const handleCopyDocument = async () => {
    try {
      await navigator.clipboard.writeText(oplDocument);
      console.log('OPL 文档已复制到剪贴板');
    } catch (error) {
      console.error('复制失败:', error);
    }
  };

  // 下载 OPL 文档
  const handleDownloadDocument = () => {
    const blob = new Blob([oplDocument], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'opm-diagram.opl';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    console.log('OPL 文档已下载');
  };

  // 获取语句类型的颜色
  const getStatementTypeColor = (type: string) => {
    const colors = {
      node: '#1890ff', // 实体 - 蓝色
      edge: '#52c41a', // 连接 - 绿色
      relation: '#fa8c16', // 关系 - 橙色
    };
    return colors[type as keyof typeof colors] || '#666';
  };

  // 获取语句类型的中文名称
  const getStatementTypeName = (type: string) => {
    const names = {
      node: '实体',
      edge: '连接',
      relation: '关系',
    };
    return names[type as keyof typeof names] || type;
  };

  return (
    <div className="opl-panel">
      <Card
        title=""
        size="small"
        // extra={
        //   <Space>
        //     <Tooltip title="刷新OPL">
        //       <Button
        //         size="small"
        //         onClick={() => {
        //           // 重新触发useEffect来刷新OPL
        //           const generator = new OPLGenerator(graphData);
        //           let statements: OPLStatement[];
        //           if (elementAddHistory && elementAddHistory.length > 0) {
        //             statements =
        //               generator.generateOPLStatementsWithHistory(
        //                 elementAddHistory
        //               );
        //           } else {
        //             statements = generator.generateOPLStatements();
        //           }
        //           const document = generator.generateOPLDocument();
        //           setOplStatements(statements);
        //           setOplDocument(document);
        //         }}
        //         disabled={oplStatements.length === 0}
        //       >
        //         刷新
        //       </Button>
        //     </Tooltip>
        //     <Tooltip title="复制OPL">
        //       <Button
        //         icon={<CopyOutlined />}
        //         size="small"
        //         onClick={handleCopyDocument}
        //         disabled={!oplDocument}
        //       />
        //     </Tooltip>
        //     <Tooltip title="下载OPL">
        //       <Button
        //         icon={<DownloadOutlined />}
        //         size="small"
        //         onClick={handleDownloadDocument}
        //         disabled={!oplDocument}
        //       />
        //     </Tooltip>
        //   </Space>
        // }
      >
        {oplStatements.length === 0 ? (
          <div className="text-center text-gray-500 py-8">
            <Text type="secondary">暂无OPL语句，请添加节点和连线</Text>
          </div>
        ) : (
          <div className="opl-statements">
            {/* 语句列表 - 按顺序显示，不按类型分组 */}
            {oplStatements.map((statement, index) => (
              <div
                key={`${statement.id}-${index}`}
                className={`opl-statement ${
                  highlightedStatement === statement.id ? 'highlighted' : ''
                }`}
                onMouseEnter={() => handleHighlightStatement(statement)}
                onMouseLeave={handleClearHighlight}
                onClick={() => handleFocusStatement(statement)}
              >
                <span className="statement-index">{index + 1}.</span>
                <span className="statement-text">{statement.text}</span>
                {/* <span className="statement-type">
                  {getStatementTypeName(statement.type)}
                </span> */}
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
};

export default OPLPanel;
